import React, { useState } from "react";
import useSWR from "swr";
import { api } from "@/lib/api";
import { Sparkles, Save, RefreshCw, KeyRound, CheckCircle2, Loader2, Database } from "lucide-react";

const fetcher = (u) => api.get(u).then((r) => r.data);

function IdentityCard({ title, description, settingKey, value, onSaved }) {
    const [text, setText] = useState(null); // null = not yet edited (show stored value)
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [ok, setOk] = useState(false);

    const effective = text !== null ? text : (value || "");
    const dirty = text !== null && text !== (value || "");

    const save = async () => {
        setBusy(true); setErr(null); setOk(false);
        try {
            await api.put(`/api/admin/infra/settings/${settingKey}`, { value: effective });
            setOk(true);
            setText(null);
            if (onSaved) onSaved();
            setTimeout(() => setOk(false), 4000);
        } catch (e) {
            setErr(e?.response?.data?.detail || String(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="cm-card p-4" data-testid={`local-ai-${settingKey}`}>
            <div className="cm-h3 flex items-center gap-1.5">
                <Sparkles size={13} /> {title}
            </div>
            <div className="text-[11px] text-zinc-500 mt-1 mb-3">{description}</div>
            <textarea
                value={effective}
                onChange={(e) => setText(e.target.value)}
                rows={14}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-xs font-mono text-zinc-200 focus:outline-none focus:border-zinc-600"
                data-testid={`local-ai-${settingKey}-textarea`}
            />
            <div className="flex items-center gap-3 mt-3">
                <button
                    onClick={save}
                    disabled={busy || !dirty}
                    className="cm-btn-primary text-xs px-3 py-2 disabled:opacity-50 flex items-center gap-1.5"
                    data-testid={`local-ai-${settingKey}-save`}
                >
                    {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                    Speichern &amp; ausrollen
                </button>
                {ok && <span className="text-xs text-emerald-400 flex items-center gap-1" data-testid={`local-ai-${settingKey}-ok`}><CheckCircle2 size={12} /> Gespeichert — Rollout an alle aktiven AI-Clouds läuft</span>}
                {err && <span className="text-xs text-cm-danger">{err}</span>}
            </div>
        </div>
    );
}

export default function LocalAiPanel() {
    const { data, mutate, error } = useSWR("/api/admin/infra/settings", fetcher);
    const settings = {};
    (data || []).forEach((s) => { settings[s.key] = s; });
    const val = (k) => settings[k]?.value ?? "";
    const rolloutAt = val("nc_agent_identity_rollout_at") || null;
    const [refreshing, setRefreshing] = useState(false);

    const refresh = async () => {
        setRefreshing(true);
        await mutate();
        setRefreshing(false);
    };

    const { data: cloudsData } = useSWR("/api/admin/clouds", fetcher);
    const aiClouds = (cloudsData?.clouds || cloudsData || [])
        .filter((c) => c.ai_enabled || c.ai_status === "active");
    const [kbBusy, setKbBusy] = useState(null);
    const [kbMsg, setKbMsg] = useState(null);
    const [kbErr, setKbErr] = useState(false);

    const kbSync = async (cloudId) => {
        setKbBusy(cloudId); setKbMsg(null); setKbErr(false);
        try {
            const r = await api.post(`/api/admin/clouds/${cloudId}/ai/kb-sync`);
            setKbMsg(`KB synchronisiert: ${(r.data.synced || []).length} Dateien.`);
        } catch (e) {
            setKbErr(true);
            setKbMsg(e?.response?.data?.detail || String(e));
        } finally {
            setKbBusy(null);
        }
    };

    if (error) {
        return <div className="cm-card p-4 text-cm-danger">Settings konnten nicht geladen werden: {String(error)}</div>;
    }

    return (
        <div className="space-y-4" data-testid="local-ai-panel">
            <div className="cm-card p-4 flex items-center justify-between gap-3">
                <div>
                    <div className="cm-h3 flex items-center gap-1.5"><Sparkles size={14} /> Lokal-AI — Agenten-Grundkonfiguration</div>
                    <div className="text-[11px] text-zinc-500 mt-1">
                        Identität + MCP-Anweisungen der AI-Agenten, getrennt für Souvera- und Standard-Clouds.
                        Beim Speichern wird der Text automatisch an alle aktiven AI-Clouds ausgerollt (ohne Downtime).
                    </div>
                </div>
                <button onClick={refresh} disabled={refreshing}
                    className="cm-btn-secondary text-xs px-3 py-2 flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="local-ai-refresh">
                    <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} /> Aktualisieren
                </button>
            </div>

            <IdentityCard
                title="Agenten-Identität — Souvera"
                description="Wird auf Souvera-Clouds (host-on.souvera.work u. a.) als Grundinfo des AI-Agenten gesetzt."
                settingKey="nc_agent_identity_souvera"
                value={val("nc_agent_identity_souvera")}
                onSaved={refresh}
            />
            <IdentityCard
                title="Agenten-Identität — Standard (Nextcloud)"
                description="Wird auf allen anderen AI-Clouds als Grundinfo des AI-Agenten gesetzt."
                settingKey="nc_agent_identity_default"
                value={val("nc_agent_identity_default")}
                onSaved={refresh}
            />

            <div className="cm-card p-4" data-testid="local-ai-mcp-card">
                <div className="cm-h3 flex items-center gap-1.5"><KeyRound size={13} /> MCP-Anbindung (AI-Tooling)</div>
                <div className="grid grid-cols-3 gap-3 mt-3 text-[11px]">
                    <div className="bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2">
                        <div className="text-zinc-500">MCP-Endpoint</div>
                        <div className="font-mono text-zinc-200 mt-0.5">{val("nc_mcp_base") || "—"}</div>
                    </div>
                    <div className="bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2">
                        <div className="text-zinc-500">Provision-API</div>
                        <div className="font-mono text-zinc-200 mt-0.5">{val("nc_mcp_provision_base") || "—"}</div>
                    </div>
                    <div className="bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2">
                        <div className="text-zinc-500">Master-Token</div>
                        <div className="font-mono text-zinc-200 mt-0.5">
                            {settings.nc_mcp_master_token?.is_set ? "gesetzt (maskiert)" : "—"}
                        </div>
                    </div>
                </div>
                <div className="text-[11px] text-zinc-500 mt-3">
                    Letzter Identitäts-Rollout: {rolloutAt ? new Date(rolloutAt).toLocaleString("de-DE") : "noch nie"}
                    {" · "}Jede Cloud erhält ihr eigenes Instanz-Token automatisch (slug = Cloud-ID).
                </div>
            </div>

            <div className="cm-card p-4" data-testid="local-ai-kb-card">
                <div className="cm-h3 flex items-center gap-1.5"><Database size={13} /> Wissensbasis (souvera_central)</div>
                <div className="text-[11px] text-zinc-500 mt-1 mb-3">
                    Spiegelt die Dateien aus <span className="font-mono">souvera_central/resources/ai/*.md</span> in den
                    KB-Ordner „Souvera Wissen" der Cloud (admin-User) und stößt die RAG-Indexierung an.
                </div>
                <div className="space-y-2">
                    {aiClouds.map((c) => (
                        <div key={c._id || c.id} className="flex items-center justify-between gap-3 bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2">
                            <div className="text-xs font-mono text-zinc-200">{c.primary_domain || c._id}</div>
                            <button
                                onClick={() => kbSync(c._id || c.id)}
                                disabled={kbBusy === (c._id || c.id)}
                                className="cm-btn-secondary text-xs px-3 py-1.5 flex items-center gap-1.5 disabled:opacity-50"
                                data-testid={`local-ai-kb-sync-${c._id || c.id}`}
                            >
                                {kbBusy === (c._id || c.id) ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                                KB synchronisieren
                            </button>
                        </div>
                    ))}
                    {aiClouds.length === 0 && (
                        <div className="text-xs text-zinc-500">Keine aktiven AI-Clouds.</div>
                    )}
                </div>
                {kbMsg && <div className={`text-xs mt-2 ${kbErr ? "text-cm-danger" : "text-emerald-400"}`}>{kbMsg}</div>}
            </div>
        </div>
    );
}
